"use client";

import { cn } from "@guru/shared/utils/cn";
import { motion, useReducedMotion } from "motion/react";

/**
 * The payoff of the whole product in one small lit panel: a fee ledger that
 * settles itself. Reminders go out, rows flip from "Due" (rose) to "Paid"
 * (green) on their own, and the collected bar fills — nobody chasing anyone.
 * Always-on but gentle; under prefers-reduced-motion every row resolves to its
 * paid state with no scheduled animation.
 */

const INK = "var(--brand-navy)";
const SAFFRON = "var(--brand-saffron)";
const POSITIVE = "var(--brand-positive)";
const DUE = "var(--brand-due)";

const ROWS = [
  { amount: "₹2,500" },
  { amount: "₹1,800" },
  { amount: "₹3,200" },
  { amount: "₹2,000" },
];

/** Independent looping transition — an instant no-op under reduced motion. */
const loop = (reduced: boolean, duration: number, delay = 0, repeatDelay = 0) =>
  reduced
    ? { duration: 0 }
    : {
        duration,
        delay,
        repeat: Number.POSITIVE_INFINITY,
        repeatDelay,
        ease: "easeInOut" as const,
      };

export function LedgerSettleScene({ className }: { className?: string }) {
  const reduced = !!useReducedMotion();

  const rowTop = (i: number) => 64 + i * 38;

  return (
    <div
      className={cn(
        "relative isolate w-full overflow-hidden rounded-2xl border border-positive/25",
        "bg-gradient-to-br from-positive/[0.08] via-card to-card shadow-lg",
        className
      )}
    >
      <div className="ledger-grid absolute inset-0 opacity-50" aria-hidden="true" />
      <div
        className="absolute -right-10 -top-12 h-32 w-32 rounded-full bg-positive/25 blur-3xl"
        aria-hidden="true"
      />

      <svg
        viewBox="0 0 320 268"
        className="relative z-10 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="A fee ledger settling itself: reminders go out and each student's balance flips from due to paid, with the collected total filling up."
      >
        {/* Header: title bar + a pulsing 'auto' pill standing in for reminders */}
        <text x="20" y="34" fontSize="15" fontWeight="700" fill={INK}>
          This month's fees
        </text>
        <motion.g
          initial={false}
          animate={reduced ? { opacity: 1 } : { opacity: [0.55, 1, 0.55] }}
          transition={loop(reduced, 2.4)}
        >
          <rect
            x="232"
            y="20"
            width="68"
            height="20"
            rx="10"
            fill={SAFFRON}
            fillOpacity="0.16"
            stroke={SAFFRON}
            strokeWidth="1.2"
          />
          <circle cx="246" cy="30" r="3.4" fill={SAFFRON} />
          <text x="256" y="34" fontSize="10.5" fontWeight="700" fill="var(--brand-saffron-text)">
            auto
          </text>
        </motion.g>

        {/* Rows */}
        {ROWS.map((row, i) => {
          const y = rowTop(i);
          const mid = y + 14;
          return (
            <g key={row.amount}>
              {/* Row surface */}
              <rect
                x="16"
                y={y}
                width="288"
                height="28"
                rx="7"
                fill="var(--card)"
                stroke={INK}
                strokeOpacity="0.1"
                strokeWidth="1"
              />
              {/* Avatar */}
              <circle cx="34" cy={mid} r="9" fill={INK} fillOpacity="0.1" />
              <circle cx="34" cy={mid - 2} r="3.2" fill={INK} fillOpacity="0.5" />
              <path d={`M28 ${mid + 6} a6 5 0 0 1 12 0`} fill={INK} fillOpacity="0.5" />
              {/* Name bars */}
              <rect x="50" y={mid - 5} width="64" height="5" rx="2.5" fill={INK} opacity="0.26" />
              <rect x="50" y={mid + 3} width="40" height="4" rx="2" fill={INK} opacity="0.14" />
              {/* Amount */}
              <text
                x="166"
                y={mid + 4}
                fontSize="11.5"
                fontWeight="700"
                fill={INK}
                fillOpacity="0.75"
              >
                {row.amount}
              </text>

              {/* Due chip — fades out as the row settles, then the loop resets */}
              <motion.g
                initial={false}
                animate={reduced ? { opacity: 0 } : { opacity: [1, 0, 0] }}
                transition={loop(reduced, 4, 0.3 + i * 0.45, 1.2)}
              >
                <rect
                  x="232"
                  y={mid - 10}
                  width="62"
                  height="20"
                  rx="10"
                  fill={DUE}
                  fillOpacity="0.12"
                  stroke={DUE}
                  strokeWidth="1.2"
                />
                <text
                  x="263"
                  y={mid + 4}
                  textAnchor="middle"
                  fontSize="10.5"
                  fontWeight="700"
                  fill={DUE}
                >
                  Due
                </text>
              </motion.g>

              {/* Paid chip — pops in once the reminder lands and holds */}
              <motion.g
                initial={false}
                animate={
                  reduced ? { opacity: 1, scale: 1 } : { opacity: [0, 1, 1], scale: [0.8, 1.08, 1] }
                }
                transition={loop(reduced, 4, 0.3 + i * 0.45, 1.2)}
                style={{ transformOrigin: `263px ${mid}px` }}
              >
                <rect
                  x="232"
                  y={mid - 10}
                  width="62"
                  height="20"
                  rx="10"
                  fill={POSITIVE}
                  fillOpacity="0.14"
                  stroke={POSITIVE}
                  strokeWidth="1.2"
                />
                <path
                  d={`M244 ${mid} l3 3 l6 -7`}
                  fill="none"
                  stroke={POSITIVE}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <text
                  x="270"
                  y={mid + 4}
                  textAnchor="middle"
                  fontSize="10.5"
                  fontWeight="700"
                  fill={POSITIVE}
                >
                  Paid
                </text>
              </motion.g>
            </g>
          );
        })}

        {/* Collected progress bar */}
        <text x="20" y="248" fontSize="11" fontWeight="600" fill={INK} fillOpacity="0.7">
          Collected
        </text>
        <rect x="92" y="240" width="208" height="10" rx="5" fill={INK} fillOpacity="0.08" />
        <motion.rect
          x="92"
          y="240"
          height="10"
          rx="5"
          fill={POSITIVE}
          initial={false}
          animate={reduced ? { width: 208 } : { width: [0, 208, 208] }}
          transition={loop(reduced, 4, 0.5, 1.2)}
        />
      </svg>
    </div>
  );
}
